/**
 * Club Detail Screen
 *
 * Detalhes de um clube específico
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	MapPin,
	Users,
	Calendar,
	Trash2,
	Phone,
	Star,
} from 'lucide-react'
import Header from '../organisms/Header'
import Card from '../atoms/Card'
import Badge from '../atoms/Badge'
import Button from '../atoms/Button'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { ConfirmModal } from '../molecules/Modal'
import DataTable from '../organisms/DataTable'
import { clubsApi } from '../../core/admin.api'
import { ADMIN_ROUTES } from '../../core/admin.constants'
import { formatDate, formatPhoneNumber, formatCurrency } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function ClubDetailScreen() {
	const { id } = useParams()
	const navigate = useNavigate()
	const [club, setClub] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const fetchClub = useCallback(async () => {
		try {
			setLoading(true)
			const response = await clubsApi.getById(id)
			setClub(response.club || response)
		} catch (err) {
			setError(err.response?.data?.message || 'Erro ao carregar clube')
		} finally {
			setLoading(false)
		}
	}, [id])

	useEffect(() => {
		fetchClub()
	}, [fetchClub])

	const handleDelete = async () => {
		setActionLoading(true)
		try {
			await clubsApi.delete(id)
			toast.success('Clube excluído com sucesso')
			navigate(ADMIN_ROUTES.CLUBS)
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir clube')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading) {
		return (
			<div className="min-h-screen">
				<Header title="Carregando..." />
				<LoadingScreen />
			</div>
		)
	}

	if (error || !club) {
		return (
			<div className="min-h-screen">
				<Header title="Erro" />
				<div className="p-6">
					<Card className="text-center py-12">
						<p className="text-red-600 mb-4">{error || 'Clube não encontrado'}</p>
						<Button onClick={() => navigate(ADMIN_ROUTES.CLUBS)}>
							Voltar
						</Button>
					</Card>
				</div>
			</div>
		)
	}

	// Colunas para membros
	const memberColumns = [
		{
			key: 'member',
			label: 'Membro',
			render: (row) => (
				<div className="flex items-center gap-3">
					<Avatar
						src={row.avatar}
						name={row.name}
						size="sm"
					/>
					<div>
						<p className="font-medium text-text">{row.name} {row.lastname || ''}</p>
						<p className="text-sm text-text-body">{row.email}</p>
					</div>
				</div>
			),
		},
		{
			key: 'role',
			label: 'Função',
			render: (row) => {
				const isOwner = club.owner?._id === row._id
				return (
					<Badge variant={isOwner ? 'green' : 'gray'}>
						{isOwner ? 'Dono' : 'Membro'}
					</Badge>
				)
			},
		},
	]

	return (
		<div className="min-h-screen">
			<Header
				title="Detalhes do Clube"
				actions={
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigate(ADMIN_ROUTES.CLUBS)}
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Voltar
					</Button>
				}
			/>

			<div className="p-6 space-y-6">
				{/* Header do clube */}
				<Card className="p-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<div className="flex items-center gap-4">
							<Avatar
								src={club.image}
								name={club.name}
								size="xl"
							/>
							<div>
							<h2 className="text-xl font-bold text-text">{club.name}</h2>
							{club.address && (
								<p className="text-text-body flex items-center gap-1">
										<MapPin className="w-4 h-4" />
										{club.address.street}, {club.address.number} - {club.address.neighborhood}
										<br />
										{club.address.city}, {club.address.state} - {club.address.zip}
									</p>
								)}
								<div className="flex items-center gap-4 mt-2">
									<Badge variant="blue">
										<Users className="w-3 h-3 mr-1" />
										{club.players?.length || 0} membros
									</Badge>
									<Badge variant="purple">
										<Calendar className="w-3 h-3 mr-1" />
										{club.matchesCount || 0} partidas
									</Badge>
								</div>
							</div>
						</div>

						<Button
							variant="danger"
							size="sm"
							onClick={() => setShowDeleteModal(true)}
						>
							<Trash2 className="w-4 h-4 mr-2" />
							Excluir
						</Button>
					</div>
				</Card>

				{/* Informações */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Dados do clube */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Informações</h3>
						<div className="space-y-4">
							<InfoRow
								icon={Calendar}
								label="Criado em"
								value={formatDate(club.createdAt, "dd/MM/yyyy 'às' HH:mm")}
							/>
							{club.phone && (
								<InfoRow
									icon={Phone}
									label="Telefone"
									value={formatPhoneNumber(club.phone)}
								/>
							)}
							{club.matchFee && (
								<InfoRow
									icon={Star}
									label="Taxa de partida"
									value={formatCurrency(club.matchFee)}
								/>
							)}
						</div>
					</Card>

					{/* Dono */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Dono do Clube</h3>
						{club.owner ? (
							<div
								className="flex items-center gap-4 p-4 bg-base-400 rounded-lg cursor-pointer hover:bg-base-400 transition-colors"
								onClick={() => navigate(`${ADMIN_ROUTES.USERS}/${club.owner._id}`)}
							>
								<Avatar
									src={club.owner.avatar}
									name={club.owner.name}
									size="lg"
								/>
								<div>
									<p className="font-medium text-text">
										{club.owner.name} {club.owner.lastname || ''}
									</p>
									<p className="text-sm text-text-body">{club.owner.email}</p>
								</div>
							</div>
						) : (
							<p className="text-text-body">Sem dono definido</p>
						)}
					</Card>
				</div>

				{/* Membros */}
				<Card className="p-6">
					<h3 className="text-lg font-semibold mb-4">
						Membros ({club.players?.length || 0})
					</h3>
					{club.players && club.players.length > 0 ? (
						<DataTable
							columns={memberColumns}
							data={club.players}
							emptyMessage="Nenhum membro"
						/>
					) : (
						<p className="text-text-body text-center py-8">
							Nenhum membro cadastrado
						</p>
					)}
				</Card>
			</div>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Clube"
				message={`Tem certeza que deseja excluir o clube "${club.name}"? Todos os dados relacionados (partidas, membros, etc.) serão removidos. Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}

// Componente auxiliar para linha de informação
function InfoRow({ icon: Icon, label, value }) {
	return (
		<div className="flex items-start gap-3">
			<div className="p-2 bg-base-400 rounded-lg">
				<Icon className="w-4 h-4 text-text-body" />
			</div>
			<div>
				<p className="text-sm text-text-body">{label}</p>
				<p className="font-medium text-text">{value}</p>
			</div>
		</div>
	)
}
